import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTaskDialogStore, useUndoStore } from "../stores/index.js";
import { useCalendarNavStore } from "../stores/calendarNav.store.js";
import { useSettingsStore, type ShortcutAction } from "../stores/settings.store.js";
import { chordFromEvent } from "./shortcut-chord.js";

/** App-wide keyboard shortcuts, mounted once in Shell. All bindings (including
 *  undo's `mod+z`) are user-rebindable — read live from settings as normalized
 *  chords and matched against the event's chord. Inert while typing in a field
 *  (so e.g. native text-undo wins) or when a modal dialog is open. Defaults:
 *  n=new task, t=Today, p=Priorities, j=prev / k=next calendar period, ?=help,
 *  mod+z=undo. */
export function useGlobalKeyboardShortcuts(onShowHelp: () => void): void {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const chord = chordFromEvent(e);
      const shortcuts = useSettingsStore.getState().shortcuts;
      const action = (Object.keys(shortcuts) as ShortcutAction[]).find((a) => shortcuts[a] === chord);
      if (!action) return; // unbound combo — let the browser handle it
      const t = e.target as HTMLElement | null;
      const inField = !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (inField || document.querySelector('[role="dialog"], [role="menu"]')) return; // field / overlay owns the keyboard
      e.preventDefault();
      switch (action) {
        case "newTask": useTaskDialogStore.getState().openCreate(); break;
        case "today": navigate("/today"); break;
        case "priorities": navigate("/priorities"); break;
        case "calNext": useCalendarNavStore.getState().step?.(1); break;
        case "calPrev": useCalendarNavStore.getState().step?.(-1); break;
        case "help": onShowHelp(); break;
        case "undo": void useUndoStore.getState().undo(); break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, onShowHelp]);
}
